import { useQueryParams } from '@moda/portal-stanchions';
import { useCallback, useEffect, useMemo } from 'react';
import { last } from 'ramda';
import { useDesktopNavigation } from '../../hooks/useNavigation';

const QUERY_PARAM_NAMING = 'navigation';

export const useDesktopNavigationV2 = () => {
  const { queryParams, setQueryParams } = useQueryParams();

  const { data, loading, error } = useDesktopNavigation();

  const selectedTab = useMemo(() => {
    const { [QUERY_PARAM_NAMING]: selectedTabQueryParameter } = queryParams;

    if (Array.isArray(selectedTabQueryParameter)) {
      return last(selectedTabQueryParameter);
    }

    if (typeof selectedTabQueryParameter === 'string') {
      return selectedTabQueryParameter;
    }

    return undefined;
  }, [queryParams]);

  const selectedVerticalData = useMemo(() => {
    if (!data) return;

    return data.verticals.find(vertical => vertical.title.toLowerCase() === selectedTab);
  }, [data, selectedTab]);

  const setSelectedTabQueryParam = useCallback(
    (value: string) => {
      setQueryParams({
        ...queryParams,
        [QUERY_PARAM_NAMING]: value.toLowerCase()
      });
    },
    [queryParams, setQueryParams]
  );

  const setDefaultVerticalAsQueryParam = useCallback(() => {
    if (!data) return;

    if (!data.verticals) return;

    setSelectedTabQueryParam(data.verticals[0].title);
  }, [data, setSelectedTabQueryParam]);

  useEffect(() => {
    if (selectedTab) return;

    setDefaultVerticalAsQueryParam();
  }, [selectedTab, setDefaultVerticalAsQueryParam]);

  const isSelectedTabQueryParamCorrect = useCallback(() => {
    if (!selectedTab) return false;

    if (!data) return false;

    if (!data.verticals.length) return false;

    return Boolean(data.verticals.find(({ title }) => title.toLowerCase() === selectedTab));
  }, [data, selectedTab]);

  useEffect(() => {
    if (isSelectedTabQueryParamCorrect()) return;

    setDefaultVerticalAsQueryParam();
  }, [isSelectedTabQueryParamCorrect, setDefaultVerticalAsQueryParam]);

  const handleSelect = useCallback(
    (verticalTitle: string) => setSelectedTabQueryParam(verticalTitle.toLowerCase()),
    [setSelectedTabQueryParam]
  );

  return { data, loading, error, selectedTab, selectedVerticalData, handleSelect };
};
